import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import { CheckCircleIcon, ArrowLeftIcon, SparklesIcon } from '@heroicons/react/24/outline';
import DiscProfile, { computeDisc } from '../../components/shared/DiscProfile';
import api from '../../api/client';
import {
  CATEGORY_HEADERS, QuestionCard, isAnswerComplete, seedRandomRankings,
} from '../../components/SurveyQuestionRenderer';

// Talent & Culture Assessment for existing staff. Same 23 questions
// every candidate gets, but tied to portal_users instead of candidates.
// Same scoring (resolveProfile() server-side) so admin sees results in
// the same shape as candidate Talent Profiles.

export default function MyAssessmentPage() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get('/my/assessment')
      .then(({ data: d }) => {
        if (cancelled) return;
        setData(d);
        if (!d.already_completed && d.questions) {
          setAnswers(seedRandomRankings(d.questions));
        }
      })
      .catch(() => toast.error('Could not load the assessment.'))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const setAnswer = (qid, value) => setAnswers(prev => ({ ...prev, [qid]: value }));

  const progress = useMemo(() => {
    if (!data?.questions?.length) return 0;
    const done = data.questions.filter(q => isAnswerComplete(q, answers[q.id])).length;
    return Math.round((done / data.questions.length) * 100);
  }, [data, answers]);

  const groupedQuestions = useMemo(() => {
    if (!data?.questions) return {};
    const groups = {};
    for (const q of data.questions) {
      const key = q.category in CATEGORY_HEADERS ? q.category : 'other';
      if (!groups[key]) groups[key] = [];
      groups[key].push(q);
    }
    return groups;
  }, [data]);

  const onSubmit = async (e) => {
    e.preventDefault();
    const missing = data.questions.find(q => !isAnswerComplete(q, answers[q.id]));
    if (missing) {
      toast.error('Please answer every required question before submitting.');
      const el = document.getElementById(`q-${missing.id}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    setSubmitting(true);
    try {
      const responses = data.questions.map(q => ({
        question_id: q.id,
        question_display_order: q.display_order,
        question_text: q.question_text,
        question_type: q.question_type,
        answer: answers[q.id] ?? null,
      }));
      await api.post('/my/assessment', { responses });
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Something went wrong. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" /></div>;
  }
  if (!data) return null;

  // Already-completed view (or just-submitted). Per director (2026-06-04):
  // staff DO see their DISC result (added with migration 042). Talent
  // Profile (Stabilizer/Caregiver/etc.) still stays hidden from staff.
  if (data.already_completed || submitted) {
    const disc = computeDisc(data.scores);
    return (
      <div className="animate-slide-up max-w-2xl mx-auto">
        <Link to="/dashboard" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4">
          <ArrowLeftIcon className="w-4 h-4" /> Back to dashboard
        </Link>
        <div className="bg-white border border-gray-200 rounded-2xl p-8 text-center">
          <div className="bg-green-50 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
            <CheckCircleIcon className="w-10 h-10 text-green-500" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Thanks — your assessment is on file</h1>
          <p className="text-gray-600">
            We appreciate you taking the time. Your responses help us understand how to support you best.
          </p>
          {data.completed_at && (
            <p className="text-xs text-gray-400 mt-6">
              Completed {new Date(data.completed_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          )}
        </div>

        {disc && (
          <div className="bg-white border border-gray-200 rounded-2xl p-6 mt-4">
            <div className="flex items-center gap-2 mb-4">
              <SparklesIcon className="w-5 h-5 text-orange-500" />
              <h2 className="text-base font-semibold text-gray-900">Your DISC profile</h2>
            </div>
            <DiscProfile scores={data.scores} />
          </div>
        )}
      </div>
    );
  }

  // Active form — wrapped in a dark container that matches the candidate
  // survey look. The shared QuestionCard uses dark styling.
  return (
    <div className="-mx-4 md:-mx-8 -mt-4 md:-mt-8 min-h-screen bg-gray-900 p-6">
      <Toaster position="top-right" />

      <div className="sticky top-0 z-30 -mx-6 px-6 pb-3 pt-2 bg-gray-900/95 backdrop-blur border-b border-gray-800">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center justify-between text-xs text-gray-400 mb-2">
            <span>Talent &amp; Culture Assessment</span>
            <span>{progress}% complete</span>
          </div>
          <div className="h-1.5 bg-gray-800 rounded-full overflow-hidden">
            <div className="h-full bg-orange-500 transition-all duration-300" style={{ width: `${progress}%` }} />
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto pt-6">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-orange-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <span className="text-white font-bold text-xl">BBC</span>
          </div>
          <h1 className="text-2xl font-bold text-white">Bright Beginnings Talent &amp; Culture Assessment</h1>
          <p className="text-gray-400 text-sm mt-2">
            Hi {data.employee?.first_name}, this is the same assessment our candidates take. It helps us know what you bring to the team and how to support you best. About 8–10 minutes.
          </p>
        </div>

        <form onSubmit={onSubmit} className="space-y-8">
          {Object.entries(groupedQuestions).map(([cat, qs]) => {
            const header = CATEGORY_HEADERS[cat] || { label: 'More questions', hint: '' };
            return (
              <div key={cat} className="space-y-3">
                <div className="flex items-baseline justify-between mb-1">
                  <h2 className="text-xs font-bold text-gray-500 uppercase tracking-wider">{header.label}</h2>
                </div>
                {header.hint && <p className="text-sm text-gray-500 -mt-1 mb-2">{header.hint}</p>}
                {qs.map(q => (
                  <QuestionCard
                    key={q.id}
                    question={q}
                    answer={answers[q.id]}
                    setAnswer={(v) => setAnswer(q.id, v)}
                  />
                ))}
              </div>
            );
          })}

          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-semibold py-3 px-6 rounded-xl transition-colors"
          >
            {submitting
              ? 'Submitting…'
              : progress < 100
                ? `Find what's still unfinished (${progress}% done)`
                : 'Submit Assessment'}
          </button>
          {progress < 100 && (
            <p className="text-center text-xs text-gray-500 mt-2">
              Tap the orange button and we'll scroll you to the question that still needs an answer.
            </p>
          )}
        </form>

        <p className="text-center text-gray-600 text-sm mt-6 mb-2">
          Bright Beginnings Childcare — Confidential
        </p>
      </div>
    </div>
  );
}
